import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { Link, navigate } from '../lib/router';
import { NoticeProvider, useNotice } from '../shell/notice';
import { useWide } from '../shell/hooks';
import { BookCard, BookTile, type BookSummary } from '../ui/books';
import { Chip, ChipDivider, ChipRow, ChoiceChips, FilterChip, ToggleChips, InterestDots, Pill, READ_TYPES, RemovableChip, type ReadType } from '../ui/bits';
import { Segmented } from '../ui/Segmented';
import { TabRow } from '../ui/TabRow';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { Stepper } from '../ui/Stepper';
import { MoveMenu, Shelf, ViewLinks, useShelfView, type MenuItem } from '../ui/controls';
import { PreviewList } from '../ui/panels';
import { PillMenu, PillPanel, SettingMenu } from '../ui/PillMenu';
import { SectionFold, SectionFoldAll } from '../ui/SectionFold';
import { BackLink } from '../ui/BackLink';
import { AuthorLine, NameLink } from '../ui/NameLink';
import { ListFoot } from '../ui/ListFoot';
import { ToTop } from '../ui/ToTop';
import { FilterBar, FilterPanel } from '../shelves/Filters';
import { filterCount, type FilterState, type Group } from '../shelves/filters';
import '../ui/ui.css';
import '../shell/shell.css';

// The Pieces page (Phase 5c): every shared piece working on invented books, so each can be
// tried on a phone and iPad and checked by the page tests. Open without signing in: it holds
// no one's data and talks to no database. Consistency pass (Nov 1): it shows the app's own
// pieces (PillMenu, SettingMenu, BackLink, ListFoot, Move ▾, the Filters button and panel), not
// copies of its own.

const BOOKS: (BookSummary & { pct?: number; line: string; note?: string; tile: string })[] = [
  { id: 'P1', title: 'A Lantern in the Fog', author: 'Ines Halvorsen', series: 'Harbour Lights · Book 2', readType: 'light', genre: 'Mystery', library: true, pct: 42, line: 'Kindle · Libby audio · started Oct 6 · day 21', note: 'Oct 24 · The second half picks up; the ferry chapter was the best so far.', tile: '42%' },
  { id: 'P2', title: 'The Salt Atlas', author: 'Marguerite Obi', readType: 'absorbing', genre: 'Historical', pct: 18, line: 'Paper · started Oct 19 · day 8', tile: '18%' },
  { id: 'P3', title: 'Grammar of Rivers', author: 'Tomas Ekwueme', readType: 'demanding', genre: 'Literary', pct: 64, line: 'Audible · started Sep 30 · day 27', note: 'Why: a friend said it changed how she reads maps.', tile: '64%' },
  { id: 'P4', title: 'Small Engines', author: 'Pria Castellane', readType: 'study', genre: 'Science', line: 'Kindle · owned', tile: 'Up Next #3' },
  { id: 'P5', title: 'Quiet Orchard', author: 'Hollis Brandt', readType: null, genre: 'Fiction', line: 'Not owned yet', tile: 'Up Next #4' },
];

function Section({ title, children, note }: { title: string; note?: string; children: ComponentChildren }) {
  return (
    <section class="pieces-sec" aria-label={title}>
      <h2>{title}</h2>
      {note ? <p class="pieces-note">{note}</p> : null}
      {children}
    </section>
  );
}

// A long panel in the catalog's groups (filters review F16): headings on a laptop and iPad, folds on a phone.
const FILTER_GROUPS: Group[] = [
  { key: 'literary', row: 'Literary genre', label: 'Literary genre', hideable: true, section: 'book', chips: [{ id: 'fiction', label: 'Fiction', count: 96 }, { id: 'nonfiction', label: 'Nonfiction', count: 24 }] },
  { key: 'genre', row: 'Genre', label: 'Genre', hideable: true, section: 'book', chips: [{ id: 'mystery', label: 'Mystery', count: 41 }, { id: 'historical', label: 'Historical', count: 18 }, { id: 'science', label: 'Science', count: 9 }] },
  { key: 'readtype', row: 'Read type', label: 'Read type', hideable: false, section: 'reading', chips: [...READ_TYPES.map((r) => ({ id: r.key, label: r.name, count: 3 + r.name.length })), { id: 'none', label: 'No read type', count: 2 }] },
  { key: 'marks', row: 'Marks', label: 'Marks', hideable: false, section: 'reading', chips: [{ id: 'read_again', label: '↻ Read again', count: 7 }, { id: 'try_again', label: '↺ Try again', count: 4 }, { id: 'favorite', label: '♥ Favorites', count: 12 }] },
  { key: 'copies', row: 'Copies', label: 'Copies', hideable: false, section: 'copies', chips: [{ id: 'owned', label: 'Owned', count: 112 }, { id: 'not_owned', label: 'Not owned yet', count: 30 }] },
  { key: 'library', row: 'Library', label: 'Library', hideable: false, section: 'copies', chips: [{ id: 'ready', label: 'Ready', count: 2, needs: true, tip: 'Needs you: 2 ready' }, { id: 'on_hold', label: 'On hold', count: 6 }] },
  { key: 'shelf', row: 'Shelf', label: 'Shelf', hideable: true, section: 'when', chips: [{ id: 'up_next', label: 'Up Next', count: 40 }, { id: 'no_status', label: 'No shelf yet', count: 7, worth: true, tip: 'Worth a look: 7 with no shelf yet' }] },
];

const SORTS: ['order' | 'interest' | 'title', string][] = [
  ['order', 'Reading order'],
  ['interest', 'Interest'],
  ['title', 'Title'],
];

type ReviewList = 'fix' | 'new' | 'match' | 'notfound' | 'summary' | 'cover';
const REVIEW_GROUPS: { label: string; options: { key: ReviewList; name: string; count: number; need?: string; worth?: string }[] }[] = [
  {
    label: 'To review',
    options: [
      { key: 'fix', name: 'Suggested in a fix', count: 377, worth: 'Worth a look: 377 in Suggested in a fix' },
      { key: 'new', name: 'New books', count: 23, worth: 'Worth a look: 23 in New books' },
    ],
  },
  {
    label: 'Lookups',
    options: [
      { key: 'match', name: 'Pick the match', count: 45, worth: 'Worth a look: 45 in Pick the match' },
      { key: 'notfound', name: 'Not found', count: 25, worth: 'Worth a look: 25 in Not found' },
      { key: 'summary', name: 'No summary', count: 102, worth: 'Worth a look: 102 in No summary' },
      { key: 'cover', name: 'No cover', count: 12, worth: 'Worth a look: 12 in No cover' },
    ],
  },
];

type MbTab = 'library' | 'owned' | 'add' | 'review';
const MB_TABS: { key: MbTab; name: string; short: string; need?: string }[] = [
  { key: 'library', name: 'Library copies', short: 'Library', need: '1 loan ended · 2 ready' },
  { key: 'owned', name: 'Owned copies', short: 'Owned' },
  { key: 'add', name: 'Add & import', short: 'Add' },
  { key: 'review', name: 'Update details', short: 'Details' },
];

const FORMATS: [string, string, number][] = [
  ['kindle', 'Kindle', 3],
  ['paper', 'Paper', 26],
  ['pdf', 'PDF / EPUB', 1],
  ['audible', 'Audible', 0],
];
// Signal chips (filters review F2): needs you (a dot) or worth a look (a ring); white until on.
const SIGNAL_SAMPLES: { key: string; label: string; count: number; need?: string; worth?: string }[] = [
  { key: 'ended', label: 'Loan ended', count: 1, need: 'Needs you: 1 loan ended' },
  { key: 'ready', label: 'Ready', count: 2, need: 'Needs you: 2 ready' },
  { key: 'place', label: 'Not placed yet', count: 23, worth: 'Worth a look: 23 not placed yet' },
  { key: 'id', label: 'Missing an ID', count: 1, worth: 'Worth a look: 1 missing an ID' },
];
const fmtShowing = (on: Record<string, boolean>) => {
  const picked = FORMATS.filter(([k]) => on[k]);
  const n = (picked.length ? picked : FORMATS).reduce((t, [, , c]) => t + c, 0);
  return `${n} ${n === 1 ? 'copy' : 'copies'}${picked.length ? '' : ' (none on: all)'}`;
};

type TableKey = 'rank' | 'title' | 'readtype' | 'gr';
const TABLE_COLS: Col<TableKey>[] = [
  { label: '', width: '22px' },
  { label: 'Rank', width: '56px', sort: 'rank' },
  { label: 'Book', width: 'minmax(0, 1fr)', sort: 'title', book: true },
  { label: 'Read type', width: '110px', sort: 'readtype' },
  { label: '★', width: '64px', sort: 'gr', num: true, title: 'Goodreads' },
  { width: '96px' },
];
const TABLE_ROWS: (BookSummary & { rank: number; gr: number; copy: string; need?: string; quiet?: boolean })[] = [
  { ...BOOKS[3]!, id: 'T1', rank: 1, gr: 4.12, copy: 'Kindle' },
  { ...BOOKS[4]!, id: 'T2', rank: 2, gr: 3.99, copy: 'Not owned yet', need: 'Needs you: no read type' },
  { ...BOOKS[1]!, id: 'T3', rank: 3, gr: 3.71, copy: 'Paper' },
  { ...BOOKS[2]!, id: 'T4', rank: 4, gr: 4.3, copy: 'Given away', quiet: true },
];
const RT_ORDER = ['light', 'absorbing', 'demanding', 'study'];
const sortRows = (s: { key: TableKey; desc: boolean }) => {
  const by = (r: (typeof TABLE_ROWS)[number]) => (s.key === 'rank' ? r.rank : s.key === 'gr' ? r.gr : s.key === 'title' ? r.title : RT_ORDER.indexOf(r.readType ?? '') + 1 || 9);
  const out = [...TABLE_ROWS].sort((a, b) => (by(a) < by(b) ? -1 : by(a) > by(b) ? 1 : 0));
  return s.desc ? out.reverse() : out;
};
const PAST_COLS: Col[] = [
  { label: 'Import', width: 'minmax(0, 1fr)' },
  { label: 'Date', width: '80px' },
  { label: 'Added', width: '60px', num: true },
  { width: '130px' },
];
const PAST = [
  { name: 'Sheet import, run 3', date: 'Oct 29', added: '12' },
  { name: 'Pasted list', date: 'Oct 12', added: '4' },
];

type StepKey = 'from' | 'type' | 'check' | 'where';
const STEPS: { key: StepKey; name: string }[] = [
  { key: 'from', name: 'Where from' },
  { key: 'type', name: 'Type or paste' },
  { key: 'check', name: 'Check the matches' },
  { key: 'where', name: 'Where they go' },
];

function PiecesBody() {
  const notice = useNotice();
  const open = (b: BookSummary) => () => notice({ message: `${b.title} would open here (the book page).`, done: false });
  const moved = (b: BookSummary, where: string) => () =>
    notice({ message: `${b.title} moved to ${where}`, undo: () => notice({ message: `${b.title} is back where it was` }) });
  const moveItems = (b: BookSummary): MenuItem[] => [
    { label: 'Currently Reading', heading: 'Move to', current: true, onSelect: () => {} },
    { label: 'Up Next', note: 'Stop reading…', onSelect: moved(b, 'Up Next') },
    { label: 'Future reads', note: 'Stop reading…', onSelect: moved(b, 'Future reads') },
    { label: 'Finished…', note: 'date, stars', onSelect: moved(b, 'Finished') },
    { label: 'Not for me…', note: 'Stop reading…', onSelect: moved(b, 'Not for me') },
  ];

  const [rt, setRt] = useState<Record<string, boolean>>({ absorbing: true });
  const [interest, setInterest] = useState<number | null>(3);
  const view = useShelfView('pieces-reading', { show: 'cards', arrange: 'one' });
  const [filters, setFilters] = useState<FilterState>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<'order' | 'interest' | 'title'>('order');
  const [shelf, setShelf] = useState<string | null>(null);
  const [shown, setShown] = useState(20);
  const [literary, setLiterary] = useState<'fiction' | 'nonfiction' | 'poetry' | null>('fiction');
  const [reasons, setReasons] = useState<string[]>(['Lost interest']);
  const [selecting, setSelecting] = useState(false);
  const matching = 120 - 17 * filterCount(filters);
  const [previewDone, setPreviewDone] = useState('');
  const [fmt, setFmt] = useState<Record<string, boolean>>({});
  const [onFilters, setOnFilters] = useState([
    { label: 'Mystery', hide: false },
    { label: 'Horror', hide: true },
  ]);
  const [hist, setHist] = useState<'book' | 'date'>('book');
  const [list, setList] = useState<ReviewList>('new');
  const [mbTab, setMbTab] = useState<MbTab>('add');
  const [six, setSix] = useState<string>('a');
  const [tSort, setTSort] = useState<{ key: TableKey; desc: boolean }>({ key: 'rank', desc: false });
  const [tSel, setTSel] = useState<string[]>(['T2']);
  const [tEmpty, setTEmpty] = useState(true);
  const [step, setStep] = useState<StepKey>('check');

  const reading = BOOKS.slice(0, 3);
  const groups: [string, typeof BOOKS][] =
    view.arrange === 'readtype'
      ? READ_TYPES.map((r) => [r.name, reading.filter((b) => b.readType === r.key)] as [string, typeof BOOKS]).filter(([, l]) => l.length)
      : [['', reading]];

  return (
    <div class="page pieces">
      <header class="pieces-head">
        <BackLink where="the app" onClick={() => navigate('/')} />
        <h1 class="page-title">Pieces</h1>
        <p class="page-question">Every shared piece of the app, working on invented books. Nothing here is saved.</p>
      </header>

      <Section title="Buttons" note="One look per job: Main (the one thing it’s for), Book action (does something to this book, or ↗ leaves the app), Secondary (menus, adding, Cancel; filled while on), Link.">
        <div class="row-wrap">
          <button type="button" class="btn-main">Start reading</button>
          <button type="button" class="btn-book">Pause</button>
          <button type="button" class="btn-book">Check in Libby ↗</button>
          <button type="button" class="btn-secondary">+ Reading note</button>
          <button type="button" class={`btn-secondary${selecting ? ' on' : ''}`} aria-pressed={selecting} onClick={() => setSelecting(!selecting)}>
            Select several
          </button>
          <button type="button" class="btn-link">Not now</button>
          <Link href="/pieces" class="btn-link">
            See all 31 ›
          </Link>
        </div>
      </Section>

      <Section title="Drop-downs" note="One pill with ▾: an action names its verb, a setting names itself and its value, an empty value is dashed. The menu marks the current choice with ✓; on a phone it opens as a sheet.">
        <div class="row-wrap">
          <SettingMenu name="Sort" choices={SORTS} value={sort} onChange={setSort} />
          <PillMenu
            label={shelf ?? 'No shelf yet'}
            ariaLabel={`Shelf: ${shelf ?? 'No shelf yet'}`}
            sheetTitle="Shelf"
            empty={!shelf}
            choices={['Up Next', 'Future reads', 'Reference'].map((x) => ({ value: x, label: x }))}
            current={shelf}
            onPick={setShelf}
          />
          <PillPanel empty label="No Kindle ID" ariaLabel="No Kindle ID for The Ministry of Time" sheetTitle="Kindle ID for The Ministry of Time">
            {(close) => (
              <>
                <label class="bk-field">
                  <span class="bk-label">Paste the Kindle link or ID</span>
                  <input type="text" placeholder="amazon.com/dp/… or B0…" />
                </label>
                <div class="bk-buttons">
                  <button type="button" class="btn-main" onClick={close}>
                    Save
                  </button>
                </div>
              </>
            )}
          </PillPanel>
        </div>
        <p class="pieces-note">A value typed or pasted in place (PillPanel): the same dashed pill, opening a small box (a sheet on a phone). Worth-a-look rows use it, “No Kindle ID ▾”, rather than the teal edge.</p>
      </Section>

      <Section
        title="A few choices in a form"
        note="ChoiceChips: one of a few (Literary genre, How you got it, Where it goes), a radio group; with clearable a second tap clears it. ToggleChips: several of a few (your genres, Not for me’s reasons). Not for narrowing a list (filter chips) or choosing a view (a switch)."
      >
        <ChoiceChips label="Literary genre" choices={[['fiction', 'Fiction'], ['nonfiction', 'Nonfiction'], ['poetry', 'Poetry']] as const} value={literary} onPick={setLiterary} clearable />
        <ToggleChips label="Reasons" small={false} choices={['Too long for now', 'Lost interest', 'Not what I expected']} on={reasons} onChange={setReasons} />
      </Section>

      <Section
        title="↑ Top"
        note="On any page that can grow past about two screens, once you’ve scrolled more than one: a pill at the right of the pinned bar on a laptop and iPad, a round button above the section bar on a phone (higher while Select several’s bar shows). Scroll this page to see it."
      >
        <ToTop wide={useWide()} />
        <ToTop wide={useWide()} float />
      </Section>

      <Section title="List foot" note="Showing 20 of 312 only while not all show, then Show N more · Show all · Show fewer.">
        <ListFoot shown={Math.min(shown, 312)} total={312} onMore={() => setShown(shown + 20)} onAll={() => setShown(312)} onFewer={() => setShown(20)} />
      </Section>

      <Section
        title="Filter chips"
        note="Filter chips narrow the list you’re on: outlined, filled while on, tap again to clear; several at once; none on means everything, so no “All” chip. [n] counts within the Filters result; the dot (with its tip) marks a chip that leads to something needing you; a chip with 0 hides unless it’s on. A row wraps when it fits two lines, otherwise it scrolls with the edge."
      >
        <ChipRow label="Read type">
          {READ_TYPES.map((r) => (
            <Chip
              key={r.key}
              label={r.name}
              count={3 + r.name.length}
              readType={r.key as ReadType}
              on={!!rt[r.key]}
              onToggle={() => setRt({ ...rt, [r.key]: !rt[r.key] })}
            />
          ))}
          <Chip label="Library" count={9} on={!!rt.lib} onToggle={() => setRt({ ...rt, lib: !rt.lib })} />
          <Chip label="↻ Read again" count={7} on={!!rt.ra} onToggle={() => setRt({ ...rt, ra: !rt.ra })} />
        </ChipRow>
        <ChipRow label="Signals and formats">
          {SIGNAL_SAMPLES.map((x) => (
            <FilterChip key={x.key} label={x.label} count={x.count} need={x.need} worth={x.worth} on={!!fmt[x.key]} onToggle={() => setFmt({ ...fmt, [x.key]: !fmt[x.key] })} />
          ))}
          <ChipDivider />
          {FORMATS.map(([k, label, n]) => (
            <FilterChip key={k} label={label} count={n} on={!!fmt[k]} onToggle={() => setFmt({ ...fmt, [k]: !fmt[k] })} />
          ))}
        </ChipRow>
        <p class="pieces-note">Signal chips come first: the dot is needs you (it lights the tab), the ring is worth a look (it never does). Both are white until on; the same chip sits in its Filters row. A divider, then the view's own chips.</p>
        <p class="pieces-note">Showing {fmtShowing(fmt)}. Audible [0] stays hidden until it has copies.</p>
        <ChipRow label="Quick filters" scroll>
          {['Not owned yet [3]', 'On Up Next 6+ months [2]', 'Interest not set [1]', 'No copy yet [4]', 'Library hold [2]'].map((t) => {
            const [label, n] = t.split(' [');
            return <Chip key={t} label={label!} count={Number(n!.replace(']', ''))} on={!!rt[t]} onToggle={() => setRt({ ...rt, [t]: !rt[t] })} />;
          })}
        </ChipRow>
      </Section>

      <Section title="Filters that are on" note="Beside Filters [n] ▾, each filter that’s on is a filled chip with ✕ (“Hide: Horror ✕” for a hidden one), then Clear all and the result in words.">
        <div class="row-wrap" role="group" aria-label="Filters that are on">
          {onFilters.map((f) => (
            <RemovableChip key={f.label} label={f.label} hide={f.hide} onRemove={() => setOnFilters(onFilters.filter((x) => x !== f))} />
          ))}
          {onFilters.length ? (
            <>
              <button
                type="button"
                class="btn-link"
                onClick={() => setOnFilters([])}
              >
                Clear all
              </button>
              <span class="pieces-note">· {212 + 40 * (2 - onFilters.length)} books</span>
            </>
          ) : (
            <button
              type="button"
              class="btn-secondary"
              onClick={() =>
                setOnFilters([
                  { label: 'Mystery', hide: false },
                  { label: 'Horror', hide: true },
                ])
              }
            >
              Put them back
            </button>
          )}
        </div>
      </Section>

      <Section
        title="View switch"
        note="A view switch chooses which list or layout you see: one shaded track, exactly one chosen, raised with 600 weight. Options may carry [n] and the dot. Past five options, a thin rule and a small label split them into groups; a long one scrolls with the edge on a phone."
      >
        <Segmented label="Show history" options={[['book', 'By book'], ['date', 'By date']]} value={hist} onChange={setHist} />
        <Segmented label="Review's lists" groups={REVIEW_GROUPS} value={list} onChange={setList} edge />
      </Section>

      <Section
        title="Tabs"
        note="Tabs sit above everything on a page; no counts, a dot while something there needs you (its tip says what). On a phone a row too wide for 358 px uses short names and the dot sits on the corner; a row that still scrolls shows the edge, and the chosen tab is always in view. A sixth tab goes behind More ▾."
      >
        <TabRow label="My books (example)" tabs={MB_TABS} value={mbTab} onPick={setMbTab} />
        <TabRow
          label="Six tabs (example)"
          tabs={['a', 'b', 'c', 'd', 'e', 'f'].map((k, i) => ({ key: k, name: `Tab ${i + 1}`, need: k === 'f' ? '1 to check' : undefined }))}
          value={six}
          onPick={setSix}
        />
      </Section>

      <Section title="The edge" note="Any row that scrolls sideways says so: the end fades into the surface with a teal ›, and ‹ once you’ve scrolled; tap an arrow to move along. At the end the fade goes. Rows that fit show nothing.">
        <ChipRow label="Letters" scroll>
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((l) => (
            <Chip key={l} label={l} on={!!rt[`l${l}`]} onToggle={() => setRt({ ...rt, [`l${l}`]: !rt[`l${l}`] })} />
          ))}
        </ChipRow>
      </Section>

      <Section
        title="Table"
        note="One table: a white sheet, a shaded small-capitals header that pins under the bar, every column named but the actions. Only columns that sort carry ⇅; the sorted one is teal with ▲ or ▼. A row that needs you has the teal edge and dot; the tint means selected; done or gone rows are grey. On a phone each row is a block: the book and its control, then one grey line. A table whose order is the information doesn’t sort and says its order above it."
      >
        <ListTable<TableKey>
          label="Up Next (example)"
          cols={TABLE_COLS}
          sort={tSort}
          onSort={(k) => setTSort(tSort.key === k ? { key: k, desc: !tSort.desc } : { key: k, desc: false })}
        >
          {sortRows(tSort).map((r) => {
            const move = (
              <PillMenu label="Move" ariaLabel={`Move ${r.title}`} choices={[{ value: 'f', label: 'Future reads' }]} onPick={moved(r, 'Future reads')} />
            );
            return (
              <ListRow
                key={r.id}
                need={r.need}
                quiet={r.quiet}
                selected={tSel.includes(r.id)}
                cells={[
                  <input type="checkbox" aria-label={`Select ${r.title}`} checked={tSel.includes(r.id)} onChange={() => setTSel(tSel.includes(r.id) ? tSel.filter((x) => x !== r.id) : [...tSel, r.id])} />,
                  `#${r.rank}`,
                  <BookCell title={r.title} author={r.author} onOpen={open(r)} />,
                  r.readType ? READ_TYPES.find((t) => t.key === r.readType)!.name : 'No read type',
                  `★ ${r.gr.toFixed(2)}`,
                  move,
                ]}
                phone={{
                  lead: `#${r.rank}`,
                  book: <BookCell title={r.title} author={r.author} onOpen={open(r)} />,
                  control: move,
                  line: [r.readType ? READ_TYPES.find((t) => t.key === r.readType)!.name : 'No read type', `★ ${r.gr.toFixed(2)}`, r.copy].join(' · '),
                }}
              />
            );
          })}
        </ListTable>
        <ListTable label="Past imports (example)" cols={PAST_COLS} order="Newest first">
          {PAST.map((p) => (
            <ListRow key={p.name} cells={[p.name, p.date, p.added, <button type="button" class="btn-secondary">Undo import…</button>]} phone={{ book: <b>{p.name}</b>, line: `${p.date} · ${p.added} added` }} />
          ))}
        </ListTable>
        <ListTable label="Filtered (example)" cols={PAST_COLS} order="Newest first" onClear={() => setTEmpty(false)}>
          {tEmpty ? [] : PAST.slice(0, 1).map((p) => <ListRow key={p.name} cells={[p.name, p.date, p.added, '']} />)}
        </ListTable>
      </Section>

      <Section title="Steps" note="A flow’s steps: numbered on a laptop, ✓ on the ones done; on a phone one line, “Step 3 of 4 · …”, with a thin bar and what’s next.">
        <Stepper steps={STEPS} current={step} onPick={setStep} canGo={(k) => STEPS.findIndex((x) => x.key === k) <= 3} />
      </Section>

      <Section title="Status pills and labels" note="Pills are facts, never buttons: one tint, told apart by their words.">
        <div class="row-wrap">
          <Pill>Due Oct 27</Pill>
          <Pill>42%</Pill>
          <Pill>↺ 2nd try</Pill>
          <Pill>↻ Read before</Pill>
          <Pill quiet>Kindle · Owned</Pill>
          <span class="rt-label">Absorbing</span>
          <span class="card-genre">Mystery</span>
          <InterestDots value={interest} onChange={setInterest} />
        </div>
      </Section>

      <Section title="Names that open a page" note="An author’s or a series’ name opens its page (with the discover flag on): on the book page, in More by, and in rows and cards. Plain words while it has no page.">
        <p>
          <NameLink kind="author" id="00000000-0000-4000-8000-000000000001">
            Jo Nesbø
          </NameLink>{' '}
          ·{' '}
          <NameLink kind="series" id="00000000-0000-4000-8000-000000000002">
            Harry Hole
          </NameLink>
        </p>
        <p class="q-sub">
          <AuthorLine author="Jo Nesbø" authorId="00000000-0000-4000-8000-000000000001" rest={['Harry Hole · Book 3']} />
        </p>
      </Section>

      <Section title="Undo notice" note="One pill under the header; Undo and ✕; closes by itself after 8 seconds.">
        <div class="row-wrap">
          <button type="button" class="btn-secondary" onClick={moved(BOOKS[1]!, 'Up Next')}>
            Make a change
          </button>
        </div>
      </Section>

      <Section title="Folding sections" note="A page with two or more folding sections (Library copies, Add & import): each fold remembered on the device, one Collapse all / Expand all for the page in its pinned bar, the count in words, and the ring on a section that’s worth a look.">
        <div class="row-wrap">
          <SectionFoldAll />
        </div>
        <SectionFold store="pieces" id="one" title="Check status" count={3}>
          <p class="pieces-note">A section’s rows.</p>
        </SectionFold>
        <SectionFold store="pieces" id="two" title="Holds to place" count={4} worth="Worth a look: 4 holds to place" closed>
          <p class="pieces-note">Folded to start.</p>
        </SectionFold>
      </Section>

      <Section title="Filters" note="The app’s Filters ▾: filled and counting [3] while any are on; the ones that are on show as chips with ✕. On a laptop the panel drops below, in groups with small headings; on a phone it fills the screen and each group folds (remembered; About the book open the first time; a group with a filter on always opens). Show / Hide only on rows of kinds. Counts are within the other rows; a chip at [0] hides unless on. The foot always says Clear all · Show N books.">
        <div class="row-wrap pieces-filters">
          <FilterBar groups={FILTER_GROUPS} state={filters} open={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} onChange={setFilters} showing={filterCount(filters) ? matching : null} />
          {filtersOpen ? <FilterPanel groups={FILTER_GROUPS} state={filters} onChange={setFilters} onClose={() => setFiltersOpen(false)} showing={matching} /> : null}
        </div>
      </Section>

      <Shelf
        id="pieces-reading"
        name="Currently Reading"
        count="3 reading"
        description="One shared shelf: fold arrow, count, view links while open."
        view={<ViewLinks view={view} />}
        seeAll={
          <Link href="/pieces" class="btn-link">
            See all 3 ›
          </Link>
        }
      >
        {groups.map(([label, list]) => (
          <div key={label} class="shelf-group">
            {label ? <span class="group-label">{label}</span> : null}
            {view.show === 'cards' ? (
              <div class="cards">
                {list.map((b) => (
                  <BookCard
                    key={b.id}
                    book={b}
                    pills={
                      <>
                        {b.library ? <Pill>Due Oct 27</Pill> : null}
                        {b.pct !== undefined ? <Pill>{b.pct}%</Pill> : null}
                      </>
                    }
                    line={b.line}
                    progress={b.pct}
                    note={b.note}
                    onOpen={open(b)}
                    actions={
                      <>
                        <button type="button" class="btn-book">
                          Open in Kindle ↗
                        </button>
                        <button type="button" class="btn-secondary">
                          + Reading note
                        </button>
                        <MoveMenu book={b.title} items={moveItems(b)} />
                      </>
                    }
                  />
                ))}
              </div>
            ) : (
              <div class="tiles">
                {list.map((b) => (
                  <BookTile key={b.id} book={b} line={b.tile} libraryPill={b.library ? 'Due 7d' : undefined} onOpen={open(b)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </Shelf>

      <Shelf id="pieces-upnext" name="Up Next" count="2 books" startOpen={false} description="Starts folded; the fold is remembered on this device.">
        <div class="tiles">
          {BOOKS.slice(3).map((b) => (
            <BookTile key={b.id} book={b} line={b.tile} onOpen={open(b)} />
          ))}
        </div>
      </Shelf>

      <Section title="Preview before saving">
        {previewDone ? (
          <p>
            {previewDone}{' '}
            <button type="button" class="btn-link" onClick={() => setPreviewDone('')}>
              Try again
            </button>
          </p>
        ) : (
          <PreviewList
            found="Found 4 highlights and 1 note · 3 new"
            rows={[
              { id: 'q1', text: '“Every harbour keeps one light for the boats it has lost.”', detail: 'Quote · Loc 1,204' },
              { id: 'q2', text: '“She folded the map until it was only the one street.”', detail: 'Quote · Loc 1,388' },
              { id: 'n1', text: 'Same image as chapter one: deliberate?', detail: 'Reading note, linked to the quote at Loc 1,388' },
              { id: 'q0', text: '“The fog had manners.”', already: true },
              { id: 'q9', text: '“Left out last time.”', already: true },
            ]}
            saveLabel={(n) => `Save ${n} ${n === 1 ? 'entry' : 'entries'}`}
            onSave={(ids) => {
              setPreviewDone(`Saved ${ids.length}.`);
              notice({ message: `Added ${ids.length} entries`, undo: () => setPreviewDone('') });
            }}
            onCancel={() => setPreviewDone('Cancelled; nothing saved.')}
          />
        )}
      </Section>
    </div>
  );
}

export function Pieces() {
  const wide = useWide();
  return (
    <NoticeProvider>
      <div class={wide ? 'shell wide' : 'shell narrow'}>
        <PiecesBody />
      </div>
    </NoticeProvider>
  );
}
